import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Volume2, Loader2, Square, LogOut } from "lucide-react";
import { FeedbackPanel } from "@/components/sim/FeedbackPanel";
import { useSceneAudio } from "@/hooks/useSceneAudio";

function shuffledOrder(n, seed) {
  const arr = Array.from({ length: n }, (_, i) => i);
  // deterministic-ish shuffle seeded by decision id so it stays stable per decision
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const SCENE_ENTER = { opacity: 0, y: 12 };
const SCENE_VISIBLE = { opacity: 1, y: 0 };
const SCENE_EXIT = { opacity: 0, y: -8 };
const SCENE_TRANSITION = { duration: 0.3, ease: "easeOut" };
const PROGRESS_TRANSITION = { duration: 0.5, ease: "easeOut" };

function sceneAudioIcon(audioState) {
  if (audioState === "loading") return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
  if (audioState === "playing") return <Square className="h-3 w-3" />;
  return <Volume2 className="h-3.5 w-3.5" />;
}

function sceneAudioLabel(audioState, client) {
  if (audioState === "playing") return "Stop";
  if (audioState === "loading") return "Preparing voice…";
  if (client === "Caller") return "Hear the caller";
  return `Hear ${client}`;
}

function choiceClass(isChosen, dim) {
  if (isChosen) return "border-emerald-600 bg-emerald-50/60 ring-2 ring-emerald-600/20";
  if (dim) return "border-stone-200 bg-white opacity-50";
  return "border-stone-200 bg-white hover:border-emerald-500 hover:bg-stone-50 cursor-pointer";
}

export const ScenarioCard = ({
  scenarioId,
  client,
  decision,
  autoNarrate,
  nextDecisionId,
  index,
  total,
  selectedIndex,
  onChoose,
  onContinue,
  onRestartCase,
  onExit,
  isLast,
  willEndEarly,
}) => {
  const revealed = selectedIndex !== null;
  const chosen = revealed ? decision.choices[selectedIndex] : null;

  const { audioState, toggleSceneAudio } = useSceneAudio(scenarioId, decision.id, autoNarrate, nextDecisionId);

  const order = useMemo(
    () => shuffledOrder(decision.choices.length, decision.id),
    [decision.id, decision.choices.length]
  );

  return (
    <section
      data-testid="scenario-card"
      className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <span data-testid="step-count" className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
          Decision {index + 1} of {total}
        </span>
        <div className="flex items-center gap-3">
          <button
            data-testid="restart-case-button"
            onClick={() => {
              if (window.confirm("Restart this case from the beginning? Your current choices will be cleared.")) onRestartCase();
            }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-400 hover:text-stone-700 transition-colors duration-200"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Restart case
          </button>
          <button
            data-testid="exit-case-button"
            onClick={onExit}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-400 hover:text-stone-700 transition-colors duration-200"
          >
            <LogOut className="h-3.5 w-3.5" /> Exit
          </button>
        </div>
      </div>

      <div
        role="progressbar"
        aria-valuenow={index + 1}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-label={`Case progress: decision ${index + 1} of ${total}`}
        className="mt-3 h-1 w-full rounded-full bg-stone-100 overflow-hidden"
      >
        <motion.div
          className="h-full rounded-full bg-emerald-700"
          initial={false}
          animate={{ width: `${((index + (revealed ? 1 : 0)) / total) * 100}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={decision.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <h2 data-testid="scenario-title" className="mt-6 font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-stone-900">
            {decision.title}
          </h2>
          <p data-testid="scenario-body" className="mt-3 text-base sm:text-lg leading-relaxed text-stone-700">
            {decision.situation}
          </p>
          <button
            data-testid="hear-scene-button"
            onClick={toggleSceneAudio}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-600 hover:border-emerald-500 hover:text-emerald-800 transition-colors duration-200"
          >
            {sceneAudioIcon(audioState)}
            {sceneAudioLabel(audioState, client)}
          </button>
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex flex-col gap-3" data-testid="choices">
        {order.map((origIdx, displayPos) => {
          const choice = decision.choices[origIdx];
          const isChosen = selectedIndex === origIdx;
          const dim = revealed && !isChosen;
          return (
            <button
              key={choice.text}
              data-testid={`choice-${displayPos}`}
              disabled={revealed}
              onClick={() => onChoose(origIdx)}
              className={`group text-left rounded-xl border p-4 min-h-[52px] transition-all duration-300 ease-out ${choiceClass(isChosen, dim)}`}
            >
              <span className="flex items-start gap-3">
                <span
                  className={`mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-full border text-xs font-semibold ${
                    isChosen ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300 text-stone-500 group-hover:border-emerald-500"
                  }`}
                >
                  {String.fromCharCode(65 + displayPos)}
                </span>
                <span className="text-[15px] leading-snug text-stone-800">{choice.text}</span>
              </span>
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {revealed && (
          <FeedbackPanel chosen={chosen} onContinue={onContinue} isLast={isLast} willEndEarly={willEndEarly} />
        )}
      </AnimatePresence>
    </section>
  );
};
